import { useEffect, useRef, useState } from 'react';
import { Input, InputGroup, InputRightAddon } from '@chakra-ui/react';

/**
 * Input para valores percentuais no formato pt-BR, com máscara automática de vírgula.
 * Os 2 primeiros dígitos digitados formam a parte inteira; a partir do 3º dígito, a vírgula
 * é inserida automaticamente e os dígitos seguintes (até 2) formam a parte decimal. O valor
 * especial 100 é aceito para permitir percentuais integrais.
 * Ex: "1" → "1" | "12" → "12" | "121" → "12,1" | "1212" → "12,12"
 * Também funciona editando um valor já preenchido: partindo de "10", digitar mais um dígito
 * gera "10,1", outro gera "10,12".
 * O `onChange` emite o valor float correspondente (ex: "12,5" → 12.5).
 *
 * @param {number}   value     - Valor numérico inicial (ex: 2.6 → exibe "2,6").
 * @param {function} onChange  - Callback com o valor float (ex: 2.6).
 * @param {string}   placeholder
 */
const MAX_INT_DIGITS = 2;
const MAX_DECIMALS = 2;

const formatValueForDisplay = (value) => {
  if (value == null || value === '') return '';
  return String(value).replace('.', ',');
};

// Máscara: os MAX_INT_DIGITS primeiros dígitos formam a parte inteira; os próximos
// MAX_DECIMALS dígitos formam a parte decimal (vírgula inserida automaticamente).
const formatDigits = (digits) => {
  if (!digits) return '';
  if (digits.length <= MAX_INT_DIGITS) return digits;
  const intPart = digits.slice(0, MAX_INT_DIGITS);
  const decPart = digits.slice(MAX_INT_DIGITS, MAX_INT_DIGITS + MAX_DECIMALS);
  return `${intPart},${decPart}`;
};

const PorcentInput = ({ value, onChange, onBlur, witdhGroup = true, placeholder = '0,00', ...props }) => {
  const [display, setDisplay] = useState(() => formatValueForDisplay(value));
  // Guarda o último valor emitido por este input pra distinguir mudanças externas de `value`
  // (ex.: dado carregado async pelo pai, cache atualizado após salvar) de mudanças que já vieram
  // do próprio onChange — sem isso, sincronizar display a cada mudança de `value` reformata o
  // texto a cada tecla digitada (perdendo zero decimal à direita enquanto o usuário ainda digita).
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    setDisplay(formatValueForDisplay(value));
  }, [value]);

  const handleChange = (e) => {
    // Extrai os dígitos do valor já mascarado (incluindo o novo dígito digitado) e limita
    // ao total de MAX_INT_DIGITS + MAX_DECIMALS.
    const rawDigits = e.target.value.replace(/\D/g, '');
    const digits = rawDigits === '100' || (display === '100' && rawDigits.startsWith('100'))
      ? '100'
      : rawDigits.slice(0, MAX_INT_DIGITS + MAX_DECIMALS);

    if (!digits) {
      setDisplay('');
      lastEmitted.current = 0;
      onChange?.(0);
      return;
    }

    const formatted = digits === '100' ? '100' : formatDigits(digits);
    setDisplay(formatted);

    const numeric = parseFloat(formatted.replace(',', '.'));
    const emitted = isNaN(numeric) ? 0 : numeric;
    lastEmitted.current = emitted;
    onChange?.(emitted);
  };

  return (
    <InputGroup w={witdhGroup ? '100%' : 'auto'}>
      <Input
        value={display}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={placeholder}
        inputMode="decimal"
        {...props}
      />
      <InputRightAddon>%</InputRightAddon>
    </InputGroup>
  );
};

export default PorcentInput;
